import { CalibrationType } from '../../config/calibration';
import api from '../../config/axios';
import { API } from '../../config/links';
import { fetchCalibrationOptions } from '../../config/calibrationOptions';
import { AuditLogItem, FormDetail, FormSchema, FormSummary, FormTag, Option, SubmissionConfig, VersionItem } from './types';
import type { Change } from './components/ChangeList';

export const formBuilderApi = {
  list: async (params?: { q?: string; status?: 'all' | 'draft' | 'published' | 'inactive'; sensor_type?: string; category?: string; tag?: FormTag }) =>
    (await api.get<FormSummary[]>(API.formBuilder.forms, { params })).data,
  get: async (key: string) => (await api.get<FormDetail>(API.formBuilder.form(key))).data,
  create: async (payload: { name: string; description?: string; sensor_type: string; category: string; tags: FormTag[]; schema: FormSchema }) =>
    (await api.post<FormDetail>(API.formBuilder.forms, payload)).data,
  update: async (key: string, payload: { name?: string; description?: string; tags?: FormTag[]; schema: FormSchema }) =>
    (await api.put<FormDetail>(API.formBuilder.form(key), payload)).data,
  duplicate: async (key: string, payload: { name: string; description?: string; sensor_type?: string; category?: string; tags?: FormTag[] }) =>
    (await api.post<FormDetail>(API.formBuilder.duplicate(key), payload)).data,
  sensorTypeOptions: async () =>
    (await fetchCalibrationOptions({ exclude_hidden_categories: true })).sensorType as Option[],
  /** Categories offered for a sensor type (hidden categories are already excluded by the API). */
  categoryOptions: async (sensorType: string) =>
    (await fetchCalibrationOptions({ sensor_type: sensorType })).category as Option[],
  remove: async (key: string) => { await api.delete(API.formBuilder.form(key)); },
  publish: async (key: string, note?: string) => (await api.post(API.formBuilder.publish(key), { note: note || null })).data,
  restore: async (key: string) => { await api.post(API.formBuilder.restore(key)); },
  versions: async (key: string) => (await api.get<VersionItem[]>(API.formBuilder.versions(key))).data,
  version: async (key: string, version: number) =>
    (await api.get<{ version: number; status: string; schema: FormSchema }>(API.formBuilder.version(key, version))).data,
  versionDiff: async (key: string, version: number, against: 'live' | 'previous' = 'live') =>
    (await api.get<Change[]>(API.formBuilder.versionDiff(key, version), { params: { against } })).data,
  rollback: async (key: string, version: number, note?: string) =>
    (await api.post<{ id: string; version: number; restored_from: number }>(API.formBuilder.rollback(key, version), { note: note || null })).data,
  auditLogs: async (key: string, params?: { limit?: number; offset?: number }) =>
    (await api.get<AuditLogItem[]>(API.formBuilder.auditLogs(key), { params })).data,
  listRuntime: async (params?: { q?: string; sensor_type?: string; category?: string; tag?: FormTag }) =>
    (await api.get<FormSummary[]>(API.formBuilder.runtimeForms, { params })).data,
  runtime: async (key: string) => (await api.get<FormSchema>(API.formBuilder.runtime(key))).data,
  resolveDataSource: async (key: string, fieldKey: string, values: Record<string, unknown>, draft: boolean | FormSchema = false) =>
    (await api.post<{ value?: unknown; options?: import('./types').Option[]; fields?: import('./types').FormField[]; data?: unknown }>(`${draft ? API.formBuilder.form(key) : API.formBuilder.runtime(key)}/data-source/${encodeURIComponent(fieldKey)}`, { values, ...(typeof draft === 'object' ? { schema: draft } : {}) })).data,
  resolveStepDynamicSchema: async (key: string, stepId: string, values: Record<string, unknown>, draft: boolean | FormSchema = false) =>
    (await api.post<{ fields?: import('./types').FormField[]; data?: unknown }>(`${draft ? API.formBuilder.form(key) : API.formBuilder.runtime(key)}/steps/${encodeURIComponent(stepId)}/dynamic-schema`, { values, ...(typeof draft === 'object' ? { schema: draft } : {}) })).data,
  submit: async (key: string, values: Record<string, unknown>, submission?: SubmissionConfig, options?: { calibrationType?: CalibrationType }) => {
    const encoding = submission?.body_encoding || 'json';
    const configuredContentType = submission?.content_type || (
      encoding === 'form_data' ? 'multipart/form-data' :
      encoding === 'urlencoded' ? 'application/x-www-form-urlencoded' :
      encoding === 'csv' ? 'text/csv' :
      encoding === 'text' ? 'text/plain' :
      'application/json'
    );

    const appendFormValue = (form: FormData, keyName: string, value: unknown) => {
      if (value == null) return;
      if (value instanceof File) {
        form.append(keyName, value, value.name);
        return;
      }
      if (Array.isArray(value) && value.some((item) => item instanceof File)) {
        value.forEach((item) => appendFormValue(form, keyName, item));
        return;
      }
      if (typeof value === 'object') {
        form.append(keyName, JSON.stringify(value));
        return;
      }
      form.append(keyName, String(value));
    };

    const csvEscape = (value: unknown) => {
      if (value == null) return '';
      if (value instanceof File) return JSON.stringify(value.name);
      const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    };

    let body: unknown = { values };
    let headers: Record<string, string | undefined> = { 'Content-Type': configuredContentType };
    // Submission intent (e.g. Re-Calibrate). Sent as a header so it works for every body encoding.
    const intentHeaders: Record<string, string> = options?.calibrationType ? { 'X-Calibration-Type': options.calibrationType } : {};

    if (encoding === 'form_data') {
      const form = new FormData();
      Object.entries(values).forEach(([fieldKey, value]) => appendFormValue(form, fieldKey, value));
      body = form;
      // Match the existing calibration upload implementation:
      // send the FormData object as multipart/form-data and let the browser/
      // Axios XHR adapter supply the multipart boundary.
      headers = { 'Content-Type': 'multipart/form-data' };
    } else if (encoding === 'urlencoded') {
      const params = new URLSearchParams();
      Object.entries(values).forEach(([fieldKey, value]) => {
        if (value == null) return;
        if (Array.isArray(value)) value.forEach((item) => params.append(fieldKey, typeof item === 'object' ? JSON.stringify(item) : String(item)));
        else params.append(fieldKey, typeof value === 'object' ? JSON.stringify(value) : String(value));
      });
      body = params;
    } else if (encoding === 'csv') {
      const keys = Object.keys(values);
      body = [
        keys.map(csvEscape).join(','),
        keys.map((keyName) => csvEscape(values[keyName])).join(','),
      ].join('\\r\\n') + '\\r\\n';
    } else if (encoding === 'text') {
      body = Object.entries(values).map(([fieldKey, value]) => `${fieldKey}=${typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')}`).join('\\n');
    }

    return (await api.post<{ success: boolean; data?: unknown; message?: string; reset_form?: boolean }>(
      API.formBuilder.runtimeSubmit(key),
      body,
      { headers: { ...headers, ...intentHeaders } },
    )).data;
  },
  validate: async (key: string, values: Record<string, unknown>) =>
    (await api.post<{ valid: boolean; errors: { field: string; message: string }[] }>(
      API.formBuilder.runtimeValidate(key), { values },
    )).data,
};
